import type { ReactNode } from 'react'
import { router } from '@inertiajs/react'

import {
  UserIcon,
  LogOutIcon,
  Monitor,
  Moon,
  Palette,
  Sun,
  UsersIcon,
} from 'lucide-react'

import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import authService from '@/lib/auth'
import { useTheme } from '@/components/theme-provider'

type Props = {
  trigger: ReactNode
  user: {
    name: string
    email: string
    avatar_url?: string | null
    admin?: boolean
  }
  defaultOpen?: boolean
  align?: 'start' | 'center' | 'end'
}

const ProfileDropdown = ({ trigger, user, defaultOpen, align = 'end' }: Props) => {
  const { setTheme } = useTheme()

  const handleLogout = async () => {
    await authService.logout()
    router.visit('/', { replace: true })
  }

  const getUserInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const initials = getUserInitials(user.name)

  return (
    <DropdownMenu defaultOpen={defaultOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent className='w-56' align={align || 'end'}>
        <DropdownMenuLabel className='flex items-center gap-3 py-2 font-normal'>
          <Avatar className='size-8'>
            <AvatarImage src={user.avatar_url || undefined} alt={user.name} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className='flex flex-1 flex-col items-start'>
            <span className='text-foreground text-sm font-medium'>{user.name}</span>
            <span className='text-muted-foreground text-xs'>{user.email}</span>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => router.visit('/profile')}>
            <UserIcon className='size-4' />
            <span>Profile</span>
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Palette className='size-4' />
              <span>Theme</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={() => setTheme('light')}>
                <Sun className='size-4' />
                Light
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('dark')}>
                <Moon className='size-4' />
                Dark
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('system')}>
                <Monitor className='size-4' />
                System
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          {user.admin && (
            <DropdownMenuItem onClick={() => router.visit('/admin/console')}>
              <UsersIcon className='size-4' />
              <span>Admin Console</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuItem variant='destructive' onClick={handleLogout}>
          <LogOutIcon className='size-4' />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default ProfileDropdown
